import type { SvgIconComponent } from '@mui/icons-material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import isEqual from 'lodash/isEqual';

import { USStates } from '@/constants/address';
import {
  EthnicOriginList,
  Gender,
  GenderList,
  LanguageList,
  NationalityList,
  RaceList,
} from '@/constants/form_inputs';
import {
  DisabilitiesCodes,
  EyeDefectsCodes,
  FaceCodes,
  FacialFeaturesCodes,
  HairTypesCodes,
} from '@/constants/users';
import {
  IMasterAddress,
  IMasterAddressPopulated,
  IMasterNameOrganization,
  IMasterNamePerson,
  IMasterVehicle,
  OrganizationType,
  PhoneNumberTypeMap,
} from '@/models';
import { MasterIndexType } from '@/pages/MasterIndex/components/MasterIndexDetailsPage/MasterIndexDetailsPage';
import { getPlainTextFromDescription } from '@/pages/Notes/NotesDataGridWrapper';
import { getAddressLocation } from '@/services';
import {
  formatCountryDisplay,
  formatDateOnly,
  formatEducationDisplay,
  formatEyeColorDisplay,
  formatMaritalStatusDisplay,
  formatPersonName,
  formatPhoneNumber,
  formatReligionDisplay,
  generateUniqueId,
} from '@/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MergeFieldDef = {
  key: string;
  label: string;
  /** Section header to render this field under in the conflict table. */
  section?: string;
  /** Special rendering mode for the conflict table cell. */
  renderType?: 'photo';
  /**
   * Virtual/grouped field: aggregates multiple record keys into one conflict row.
   * The conflict value will be a plain object keyed by each of `keys`.
   * When a choice is applied the values for all `keys` are copied wholesale.
   */
  virtual?: { keys: string[] };
  /**
   * Expand array elements into individual conflict rows, one per index.
   * The merge executor reconstructs the final array from per-item choices.
   */
  expandArray?: true;
  /**
   * Per-item equality for `expandArray` fields. Return true when two items
   * at the same index should be considered equal (no conflict). Useful when
   * full `isEqual` would produce false conflicts due to audit timestamps.
   */
  compareItem?: (a: unknown, b: unknown) => boolean;
  /**
   * Custom equality check. Return true when the two values should be considered
   * equal (no conflict). Falls back to lodash `isEqual` when omitted.
   */
  compare?: (a: unknown, b: unknown) => boolean;
  /**
   * Custom value formatter. When set, MergeConflictStep uses this instead of
   * the per-type formatter dispatch — handy for synthesized conflict rows
   * (e.g. pre-plan subfields) whose key isn't known to `formatMergeValue`.
   * Returning `null` falls back to the default formatter.
   */
  formatter?: (value: unknown) => string | null;
};

export type AnyMasterRecord =
  | IMasterNamePerson
  | IMasterAddress
  | IMasterAddressPopulated
  | IMasterNameOrganization
  | IMasterVehicle;

type MergeTypeConfig = {
  fields: MergeFieldDef[];
  getRecordTitle: (r: AnyMasterRecord) => string;
  getRecordSubtitle: (r: AnyMasterRecord) => string;
  icon: SvgIconComponent;
};

export type MergeConflict = {
  field: MergeFieldDef;
  currentValue: unknown;
  selectedValue: unknown;
  /** Set for conflicts generated by `expandArray` fields. */
  parentKey?: string;
  arrayIndex?: number;
};

// ---------------------------------------------------------------------------
// Shared utilities
// ---------------------------------------------------------------------------

const EMPTY = '-';

export function getNestedValue(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (acc, key) =>
        acc != null ? (acc as Record<string, unknown>)[key] : undefined,
      obj,
    );
}

/**
 * Write `value` at the dot-notation `path` inside `obj`, cloning each
 * intermediate object so the original is not mutated.
 */
export function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const keys = path.split('.');
  if (keys.length === 1) {
    obj[path] = value;
    return;
  }
  const [head, ...rest] = keys;
  const child =
    obj[head] != null && typeof obj[head] === 'object'
      ? { ...(obj[head] as Record<string, unknown>) }
      : {};
  obj[head] = child;
  setNestedValue(child, rest.join('.'), value);
}

function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    return Object.values(value as object).every(isEmpty);
  }
  return false;
}

function formatArrayOfCodes(
  arr: unknown,
  lookup: Record<string, string>,
): string {
  if (!Array.isArray(arr) || arr.length === 0) return EMPTY;
  return arr.map((code) => lookup[code as string] ?? String(code)).join(', ');
}

// ---------------------------------------------------------------------------
// Person - formatters
// ---------------------------------------------------------------------------

function formatSinglePhone(value: unknown): string {
  if (!value || typeof value !== 'object') return EMPTY;
  const p = value as any;
  const typeLabel =
    PhoneNumberTypeMap[p?.type as keyof typeof PhoneNumberTypeMap] ??
    p?.type ??
    '';
  const raw = p?.phoneNumber ?? '';
  const number = formatPhoneNumber(raw) || raw;
  return typeLabel ? `${typeLabel}: ${number}` : number || EMPTY;
}

function formatPersonField(key: string, value: unknown): string {
  if (isEmpty(value)) return EMPTY;

  // Expanded array item keys e.g. "phoneNumbers::0"
  if (key.includes('::')) {
    const parentKey = key.split('::')[0];
    switch (parentKey) {
      case 'phoneNumbers':
        return formatSinglePhone(value);
      case 'emails':
        return typeof value === 'string' ? value : EMPTY;
      case 'emergencyContacts':
        return formatSingleEmergencyContact(value);
      case 'addresses':
        return formatSingleAddress(value);
      default:
        return String(value);
    }
  }

  // Driver license virtual grouped key
  if (key === '__driverLicenseId') {
    if (!value || typeof value !== 'object') return EMPTY;
    const d = value as Record<string, unknown>;
    const state = d['driverLicense.state'];
    const number = d['driverLicense.number'];
    const stateLabel =
      typeof state === 'string'
        ? USStates.find((s) => s.value === state)?.label ?? state
        : null;
    return (
      [stateLabel, number ? String(number) : null]
        .filter(Boolean)
        .join(' · ') || EMPTY
    );
  }

  // Driver license individual sub-fields
  if (key.startsWith('driverLicense.')) {
    const sub = key.slice('driverLicense.'.length);
    switch (sub) {
      case 'expireDate':
      case 'issueDate':
        return formatDateOnly(String(value)) || EMPTY;
      default:
        return String(value);
    }
  }

  // Physical characteristics sub-fields (dot-notation keys)
  if (key.startsWith('physicalCharacteristics.')) {
    const sub = key.slice('physicalCharacteristics.'.length);
    switch (sub) {
      case 'weight':
        return `${value} lbs`;
      case 'height':
        return String(value);
      case 'heightExact': {
        const h = value as Record<string, unknown>;
        const parts = [
          h.feet != null ? `${h.feet}'` : null,
          h.inches != null ? `${h.inches}"` : null,
        ].filter(Boolean);
        return parts.join(' ') || EMPTY;
      }
      case 'eyeColor':
        return formatEyeColorDisplay(String(value), String(value), false);
      case 'build':
      case 'skinTone':
      case 'bodyMarking':
      case 'hairColor':
        return String(value);
      case 'eyeDefects':
        return formatArrayOfCodes(value, EyeDefectsCodes);
      case 'face':
        return formatArrayOfCodes(value, FaceCodes);
      case 'disabilities':
        return formatArrayOfCodes(value, DisabilitiesCodes);
      case 'facialFeatures':
        return formatArrayOfCodes(value, FacialFeaturesCodes);
      case 'hairTypes':
        return formatArrayOfCodes(value, HairTypesCodes);
      case 'others':
        return Array.isArray(value)
          ? (value as string[]).join(', ') || EMPTY
          : String(value);
      default:
        return String(value);
    }
  }

  switch (key) {
    case 'sex':
      return (
        GenderList[value as keyof typeof GenderList] ??
        (value === Gender.UNKNOWN ? EMPTY : String(value))
      );
    case 'race':
      return RaceList[value as keyof typeof RaceList] ?? String(value);
    case 'ethnicOrigin':
      return (
        EthnicOriginList[value as keyof typeof EthnicOriginList] ??
        String(value)
      );
    case 'nationality':
      return (
        NationalityList[value as keyof typeof NationalityList] ?? String(value)
      );
    case 'maritalStatus':
      return formatMaritalStatusDisplay(String(value), EMPTY, false);
    case 'education':
      return formatEducationDisplay(String(value), EMPTY, false);
    case 'religion':
      return formatReligionDisplay(String(value), EMPTY, false);
    case 'citizenOf':
      return formatCountryDisplay(String(value), EMPTY);
    case 'birthday':
      return formatDateOnly(String(value)) || EMPTY;
    case 'transient':
    case 'isVerified':
      return value ? 'Yes' : 'No';
    case 'spokenLanguages':
      return Array.isArray(value)
        ? value
            .map(
              (c) => LanguageList[c as keyof typeof LanguageList] ?? String(c),
            )
            .join(', ') || EMPTY
        : String(value);
    case 'phoneNumbers':
      return Array.isArray(value)
        ? value
            .map((p: any) => {
              const typeLabel =
                PhoneNumberTypeMap[
                  p?.type as keyof typeof PhoneNumberTypeMap
                ] ??
                p?.type ??
                '';
              const raw = p?.phoneNumber ?? '';
              const number = formatPhoneNumber(raw) || raw;
              return typeLabel ? `${typeLabel}: ${number}` : number;
            })
            .filter(Boolean)
            .join('\n') || EMPTY
        : String(value);
    case 'emails':
      return Array.isArray(value)
        ? (value as string[]).join('\n') || EMPTY
        : String(value);
    case 'addresses':
      return Array.isArray(value)
        ? value
            .map((a: any) => {
              const primary = a?.isPrimary ? '(Primary) ' : '';
              const addrText = getAddressLocation(a) || a?.addressLabel || '';
              return addrText ? `${primary}${addrText}` : '';
            })
            .filter(Boolean)
            .join('\n') || EMPTY
        : String(value);
    case 'employerInfo.phoneNumber':
      return formatPhoneNumber(String(value)) || String(value);
    case 'employerInfo.address':
      return (
        getAddressLocation(value as any) ||
        (value as any)?.addressLabel ||
        EMPTY
      );
    case 'emergencyContacts':
      return Array.isArray(value)
        ? value
            .map((ec: any) => {
              const p = ec?.emergencyContact;
              if (!p) return '';
              const name = formatPersonName(p);
              const rel = ec?.relationship?.relationship ?? '';
              return rel ? `${name} (${rel})` : name;
            })
            .filter(Boolean)
            .join('\n') || EMPTY
        : String(value);
    case 'socialMedia':
      return Array.isArray(value)
        ? value
            .map((s: any) =>
              s?.platform && s?.username ? `${s.platform}: ${s.username}` : '',
            )
            .filter(Boolean)
            .join('\n') || EMPTY
        : String(value);
    default:
      return String(value);
  }
}

// Compare emergency contacts by {personId + relationship} pairs, ignoring
// mutable fields like timestamps that would otherwise always produce a diff.
// Per-item equality for emergency contacts: compare by contact person ID +
// relationship + primary flag, ignoring audit timestamps that differ between records.
function compareEmergencyContactItem(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  const aId =
    (a as any)?.emergencyContact?._id ?? (a as any)?.emergencyContact?.id ?? '';
  const bId =
    (b as any)?.emergencyContact?._id ?? (b as any)?.emergencyContact?.id ?? '';
  const aRel = (a as any)?.relationship?.relationship ?? '';
  const bRel = (b as any)?.relationship?.relationship ?? '';
  const aPrimary = (a as any)?.relationship?.isPrimary ?? false;
  const bPrimary = (b as any)?.relationship?.isPrimary ?? false;
  return aId === bId && aRel === bRel && aPrimary === bPrimary;
}

function formatSingleEmergencyContact(value: unknown): string {
  if (!value || typeof value !== 'object') return EMPTY;
  const ec = value as any;
  const p = ec?.emergencyContact;
  if (!p) return EMPTY;
  const name = formatPersonName(p);
  const rel = ec?.relationship?.relationship ?? '';
  const isPrimary = ec?.relationship?.isPrimary;
  return (
    [isPrimary ? '(Primary)' : null, name || null, rel ? `(${rel})` : null]
      .filter(Boolean)
      .join(' ') || EMPTY
  );
}

function formatSingleContact(value: unknown): string {
  if (!value || typeof value !== 'object') return EMPTY;
  const c = value as any;
  const phone = c.phoneNumber
    ? formatPhoneNumber(c.phoneNumber) || c.phoneNumber
    : null;
  return [c.name || null, phone].filter(Boolean).join(' · ') || EMPTY;
}

function formatSingleAddress(value: unknown): string {
  if (!value || typeof value !== 'object') return EMPTY;
  const a = value as any;
  const isPrimary = a?.isPrimary;
  const addressStr = getAddressLocation(a) || a?.addressLabel || '';
  return (
    [isPrimary ? '(Primary)' : null, addressStr || null]
      .filter(Boolean)
      .join(' ') || EMPTY
  );
}

// Compare file arrays by _id only, across all mime types.
function compareFiles(a: unknown, b: unknown): boolean {
  const files = (v: unknown) => (Array.isArray(v) ? v : []);
  const aIds = new Set(files(a).map((f: any) => f?._id));
  const bIds = new Set(files(b).map((f: any) => f?._id));
  return aIds.size === bIds.size && [...aIds].every((id) => bIds.has(id));
}

// Two inline addresses point at the same physical location if their
// user-meaningful identity matches. Geocoding artifacts (`normalized`, `point`,
// intersectionStreets, etc.) can differ between records due to separate
// place-API calls even for the same address, so `isEqual` produces false
// conflicts. Compare on the fields the user actually controls.
function compareInlineAddressItem(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const norm = (v: any) => ({
    addressNumber: (v?.addressNumber ?? '').trim(),
    completeStreetName: (v?.completeStreetName ?? '').trim(),
    subAddress: (v?.subAddress ?? '').trim(),
    state: v?.state ?? '',
    zipcode: v?.zipcode ?? '',
    country: v?.country ?? '',
    isPrimary: !!v?.isPrimary,
  });
  return isEqual(norm(a), norm(b));
}

// Master-address records: compare every user-visible identity / display
// field. `subAddress` is intentionally excluded — it has its own conflict
// row so the user can resolve unit/apt # independently of the geographic
// location. Geocoding artifacts (`normalized`, `point`, intersection / cross
// streets, distances, municipality codes, status) are also excluded because
// they can drift between independent place-API lookups for the same address
// and would otherwise produce false conflicts.
//
// Including `addressLabel`, `zipName`, and `placeName` is what lets the
// virtual conflict surface when two records describe the same physical
// location but were entered with different display labels — without these,
// the user couldn't choose which label to keep on the merged record, and the
// downstream views (org list etc.) would keep showing the target's original
// label even though the user picked the matched record's data.
function compareMasterAddressLocation(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const norm = (v: any) => ({
    addressLabel: (v?.addressLabel ?? '').trim(),
    addressNumber: (v?.addressNumber ?? '').trim(),
    completeStreetName: (v?.completeStreetName ?? '').trim(),
    state: v?.state ?? '',
    zipcode: v?.zipcode ?? '',
    zipName: (v?.zipName ?? '').trim(),
    placeName: (v?.placeName ?? '').trim(),
    country: v?.country ?? '',
  });
  return isEqual(norm(a), norm(b));
}

// ---------------------------------------------------------------------------
// Person config
// ---------------------------------------------------------------------------

const PERSON_DL = "Driver's license/ID Card";
const PERSON_IDENTITY = 'Identity';
const PERSON_PERSONAL = 'Personal Information';
const PERSON_OTHER = 'Other information';
const PERSON_EMERGENCY = 'Emergency Contacts';
const PERSON_EMPLOYMENT = 'Employment information';
const PERSON_PHYSICAL = 'Physical Characteristics';
const PERSON_VERIFICATION = 'Verification';
const PERSON_PHOTOS = 'Photos';

const personConfig: MergeTypeConfig = {
  fields: [
    // Driver's license / ID Card
    {
      key: '__driverLicenseId',
      label: 'Driver license',
      section: PERSON_DL,
      virtual: { keys: ['driverLicense.state', 'driverLicense.number'] },
    },
    {
      key: 'driverLicense.vehicleClass',
      label: 'DL class',
      section: PERSON_DL,
    },
    {
      key: 'driverLicense.restrictionCodes',
      label: 'DL restriction codes',
      section: PERSON_DL,
    },
    {
      key: 'driverLicense.endorsementCodes',
      label: 'DL endorsement codes',
      section: PERSON_DL,
    },
    {
      key: 'driverLicense.expireDate',
      label: 'DL expiration date',
      section: PERSON_DL,
    },
    {
      key: 'driverLicense.documentNumber',
      label: 'DL document number',
      section: PERSON_DL,
    },
    {
      key: 'driverLicense.issueDate',
      label: 'DL issue date',
      section: PERSON_DL,
    },
    // Identity
    { key: 'suffix', label: 'Suffix', section: PERSON_IDENTITY },
    { key: 'lastName', label: 'Last name', section: PERSON_IDENTITY },
    { key: 'firstName', label: 'First name', section: PERSON_IDENTITY },
    { key: 'middleName', label: 'Middle name', section: PERSON_IDENTITY },
    { key: 'maidenName', label: 'Maiden name', section: PERSON_IDENTITY },
    { key: 'aliasName', label: 'Alias name', section: PERSON_IDENTITY },
    { key: 'nickName', label: 'Nickname', section: PERSON_IDENTITY },
    { key: 'birthday', label: 'Date of birth', section: PERSON_IDENTITY },
    { key: 'placeOfBirth', label: 'Place of birth', section: PERSON_IDENTITY },
    { key: 'sex', label: 'Sex', section: PERSON_IDENTITY },
    { key: 'race', label: 'Race', section: PERSON_IDENTITY },
    { key: 'religion', label: 'Religion', section: PERSON_IDENTITY },
    { key: 'education', label: 'Education', section: PERSON_IDENTITY },
    { key: 'ssn', label: 'SSN', section: PERSON_IDENTITY },
    { key: 'nysid', label: 'NYSID', section: PERSON_IDENTITY },
    { key: 'fbiNumber', label: 'FBI number', section: PERSON_IDENTITY },
    { key: 'refNumber', label: 'Ref #', section: PERSON_IDENTITY },
    { key: 'nationality', label: 'Nationality', section: PERSON_IDENTITY },
    { key: 'citizenOf', label: 'Citizen of', section: PERSON_IDENTITY },
    { key: 'ethnicOrigin', label: 'Ethnic origin', section: PERSON_IDENTITY },
    // Personal Information
    { key: 'transient', label: 'Transient', section: PERSON_PERSONAL },
    {
      key: 'addresses',
      label: 'Address',
      section: PERSON_PERSONAL,
      expandArray: true,
      compareItem: compareInlineAddressItem,
    },
    {
      key: 'phoneNumbers',
      label: 'Phone',
      section: PERSON_PERSONAL,
      expandArray: true,
    },
    // Other information
    { key: 'maritalStatus', label: 'Marital status', section: PERSON_OTHER },
    {
      key: 'spokenLanguages',
      label: 'Spoken languages',
      section: PERSON_OTHER,
    },
    { key: 'socialMedia', label: 'Social media', section: PERSON_OTHER },
    {
      key: 'emails',
      label: 'Email',
      section: PERSON_OTHER,
      expandArray: true,
    },
    {
      key: 'gangAffiliations',
      label: 'Gang affiliations',
      section: PERSON_OTHER,
    },
    // Emergency Contacts
    {
      key: 'emergencyContacts',
      label: 'Emergency contact',
      section: PERSON_EMERGENCY,
      expandArray: true,
      compareItem: compareEmergencyContactItem,
    },
    // Employment information
    {
      key: 'employerInfo.name',
      label: 'Employer name',
      section: PERSON_EMPLOYMENT,
    },
    {
      key: 'employerInfo.phoneNumber',
      label: 'Employer phone',
      section: PERSON_EMPLOYMENT,
    },
    {
      key: 'employerInfo.address',
      label: 'Employer address',
      section: PERSON_EMPLOYMENT,
    },
    {
      key: 'employerInfo.occupation',
      label: 'Occupation',
      section: PERSON_EMPLOYMENT,
    },
    // Physical Characteristics
    {
      key: 'physicalCharacteristics.weight',
      label: 'Weight',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.height',
      label: 'Height',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.heightExact',
      label: 'Height (exact)',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.build',
      label: 'Build',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.skinTone',
      label: 'Skin tone',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.face',
      label: 'Face',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.disabilities',
      label: 'Disabilities',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.eyeColor',
      label: 'Eye color',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.eyeDefects',
      label: 'Eye defects',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.hairColor',
      label: 'Hair color',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.hairTypes',
      label: 'Hair types',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.facialFeatures',
      label: 'Facial features',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.bodyMarking',
      label: 'Body marking',
      section: PERSON_PHYSICAL,
    },
    {
      key: 'physicalCharacteristics.others',
      label: 'Other characteristics',
      section: PERSON_PHYSICAL,
    },
    // Flags are merged via a dedicated per-flag section in the conflict step
    // (not a single conflict row), so they don't appear in `fields`.
    // Verification
    { key: 'isVerified', label: 'Verified', section: PERSON_VERIFICATION },
    // Photos
    {
      key: 'files',
      label: 'Photos',
      section: PERSON_PHOTOS,
      renderType: 'photo',
      compare: compareFiles,
    },
  ],
  getRecordTitle: (r) => {
    const p = r as IMasterNamePerson;
    const name = formatPersonName(p);
    const id = p.uniqueId != null ? generateUniqueId('PER', p.uniqueId) : '';
    return [id, name].filter(Boolean).join(' · ');
  },
  getRecordSubtitle: (r) => {
    const p = r as IMasterNamePerson;
    // The master-index list derives `address` from the embedded `addresses`
    // array, but the detail GET (used after selecting a record) populates the
    // scalar `address` ref — which is empty for form-saved records. Fall back
    // to the first embedded address so the selected-record card shows the
    // same address the search result did.
    const addr = (p.address ?? p.addresses?.[0]) as any;
    const addressText = addr
      ? getAddressLocation(addr) || addr?.addressLabel || ''
      : '';
    const rawPhone = p.phoneNumbers?.[0]?.phoneNumber ?? null;
    const phone = rawPhone ? formatPhoneNumber(rawPhone) || rawPhone : null;
    return [
      p.birthday ? formatDateOnly(p.birthday) : null,
      phone,
      addressText || null,
    ]
      .filter(Boolean)
      .join(' · ');
  },
  icon: PersonOutlineIcon,
};

// ---------------------------------------------------------------------------
// Address config
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Address - formatters
// ---------------------------------------------------------------------------

// `type` may be populated as { _id, name, ... } or a bare ID string.
function resolveCautionOrNoteTypeName(type: unknown): string {
  if (!type) return '';
  if (typeof type === 'object') {
    return (type as any).name ?? (type as any)._id ?? '';
  }
  return String(type);
}

function resolveCautionOrNoteTypeId(type: unknown): string {
  if (!type) return '';
  if (typeof type === 'object') return (type as any)._id ?? '';
  return String(type);
}

// `comment` is a Draft.js raw-state JSON string; convert to plain text so the
// merge UI doesn't show {"text":{"blocks":...}}.
function resolveCommentText(comment: unknown): string {
  if (!comment) return '';
  if (typeof comment !== 'string') return String(comment);
  return getPlainTextFromDescription(comment) || comment;
}

function formatSingleCautionOrNote(value: unknown): string {
  if (!value || typeof value !== 'object') return EMPTY;
  const c = value as any;
  const typeName = resolveCautionOrNoteTypeName(c.type);
  const commentText = resolveCommentText(c.comment);
  return (
    [typeName || null, commentText || null].filter(Boolean).join(' · ') || EMPTY
  );
}

// Compare cautions / address notes by semantic content. The two records being
// merged each own their own caution/note documents with distinct _ids and
// audit timestamps, so isEqual would always flag a conflict - even when the
// type + comment are identical. Comparing by content avoids those false diffs.
function compareCautionOrNoteItem(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const x = a as any;
  const y = b as any;
  return (
    resolveCautionOrNoteTypeId(x.type) === resolveCautionOrNoteTypeId(y.type) &&
    resolveCommentText(x.comment) === resolveCommentText(y.comment) &&
    (x.deleted ?? false) === (y.deleted ?? false)
  );
}

function formatAddressField(key: string, value: unknown): string {
  if (isEmpty(value)) return EMPTY;

  if (key.includes('::')) {
    const parentKey = key.split('::')[0];
    if (parentKey === 'cautions' || parentKey === 'addressNotes') {
      return formatSingleCautionOrNote(value);
    }
    return String(value);
  }

  switch (key) {
    case '__addressLocation':
      // The unit / apt # is rendered in its own conflict row, so strip it
      // from the location string here to avoid showing it twice.
      return getAddressLocation(value as any, true) || EMPTY;
    case 'subAddress':
      return typeof value === 'string' ? value : EMPTY;
    default:
      return String(value);
  }
}

// Master-address record geographic fields are all inferred from the location
// picker and auto-populated together - there's no scenario where the user
// picks state from one record and zipcode from another. Group those into a
// single virtual conflict row so the choice is made as a unit. `subAddress`
// (unit / apt #) is a separate row because two records at the same building
// can legitimately differ on it - merging unit 3 into unit 5 is a different
// decision than merging two geocodings of the same street address.
const addressConfig: MergeTypeConfig = {
  fields: [
    {
      key: '__addressLocation',
      label: 'Address',
      section: 'Address Information',
      virtual: {
        keys: [
          'addressLabel',
          'addressNumber',
          'completeStreetName',
          'state',
          'zipcode',
          'zipName',
          'country',
          'placeName',
          'normalized',
          'point',
          'pointType',
          'intersectionStreets',
          'nearestIntersection',
          'distance1',
          'distance2',
          'municipalityType',
          'crossStreet1',
          'crossStreet2',
          'status',
        ],
      },
      compare: compareMasterAddressLocation,
    },
    {
      key: 'subAddress',
      label: 'Unit / Apt #',
      section: 'Address Information',
    },
    {
      key: 'cautions',
      label: 'Caution',
      section: 'Cautions',
      expandArray: true,
      compareItem: compareCautionOrNoteItem,
    },
    {
      key: 'addressNotes',
      label: 'Address note',
      section: 'Notes',
      expandArray: true,
      compareItem: compareCautionOrNoteItem,
    },
  ],
  getRecordTitle: (r) => {
    const a = r as IMasterAddress;
    const id = a.uniqueId != null ? generateUniqueId('ADR', a.uniqueId) : '';
    const addressText = getAddressLocation(a, true) || a.addressLabel || '';
    return [id, addressText].filter(Boolean).join(' · ');
  },
  getRecordSubtitle: (r) => {
    const a = r as IMasterAddress;
    return a.subAddress ?? '';
  },
  icon: LocationOnOutlinedIcon,
};

// ---------------------------------------------------------------------------
// Organization - formatters
// ---------------------------------------------------------------------------

function formatOrgField(key: string, value: unknown): string {
  if (isEmpty(value)) return EMPTY;

  // Expanded array item keys e.g. "phoneNumbers::0"
  if (key.includes('::')) {
    const parentKey = key.split('::')[0];
    switch (parentKey) {
      case 'phoneNumbers':
        return formatSinglePhone(value);
      case 'emails':
      case 'urls':
      case 'socialMediaLinks':
        return typeof value === 'string' ? value : EMPTY;
      case 'contacts':
        return formatSingleContact(value);
      case 'emergencyContacts':
        return formatSingleEmergencyContact(value);
      case 'addresses':
        return formatSingleAddress(value);
      default:
        return String(value);
    }
  }

  switch (key) {
    case 'organizationType':
      return (
        OrganizationType[value as keyof typeof OrganizationType] ??
        String(value)
      );
    case 'phoneNumbers':
      return Array.isArray(value)
        ? value
            .map((p: any) => {
              const typeLabel =
                PhoneNumberTypeMap[
                  p?.type as keyof typeof PhoneNumberTypeMap
                ] ??
                p?.type ??
                '';
              const raw = p?.phoneNumber ?? '';
              const number = formatPhoneNumber(raw) || raw;
              return typeLabel ? `${typeLabel}: ${number}` : number;
            })
            .filter(Boolean)
            .join('\n') || EMPTY
        : String(value);
    case 'emails':
    case 'urls':
    case 'socialMediaLinks':
      return Array.isArray(value)
        ? (value as string[]).join('\n') || EMPTY
        : String(value);
    case 'addresses':
      return Array.isArray(value)
        ? value
            .map((a: any) => {
              const primary = a?.isPrimary ? '(Primary) ' : '';
              const addrText = getAddressLocation(a) || a?.addressLabel || '';
              return addrText ? `${primary}${addrText}` : '';
            })
            .filter(Boolean)
            .join('\n') || EMPTY
        : String(value);
    case 'contacts':
      return Array.isArray(value)
        ? value
            .map((c: any) => {
              const phone = c?.phoneNumber
                ? formatPhoneNumber(c.phoneNumber) || c.phoneNumber
                : null;
              return [c?.name || null, phone].filter(Boolean).join(' · ');
            })
            .filter(Boolean)
            .join('\n') || EMPTY
        : String(value);
    case 'emergencyContacts':
      return Array.isArray(value)
        ? value
            .map((ec: any) => {
              const p = ec?.emergencyContact;
              if (!p) return '';
              const name = formatPersonName(p);
              const rel = ec?.relationship?.relationship ?? '';
              return rel ? `${name} (${rel})` : name;
            })
            .filter(Boolean)
            .join('\n') || EMPTY
        : String(value);
    case 'isVerified':
      return value ? 'Yes' : 'No';
    case 'socialMedia':
      return Array.isArray(value)
        ? value
            .map((s: any) =>
              s?.platform && s?.username ? `${s.platform}: ${s.username}` : '',
            )
            .filter(Boolean)
            .join('\n') || EMPTY
        : String(value);
    default:
      return String(value);
  }
}

// ---------------------------------------------------------------------------
// Organization config
// ---------------------------------------------------------------------------

const organizationConfig: MergeTypeConfig = {
  fields: [
    // Basic information
    { key: 'name', label: 'Name', section: 'Basic information' },
    {
      key: 'organizationType',
      label: 'Organization type',
      section: 'Basic information',
    },
    { key: 'notes', label: 'Notes', section: 'Basic information' },
    {
      key: 'nationality',
      label: 'Nationality',
      section: 'Basic information',
    },
    {
      key: 'gangAffiliations',
      label: 'Gang affiliations',
      section: 'Basic information',
    },
    {
      key: 'addresses',
      label: 'Address',
      section: 'Basic information',
      expandArray: true,
      compareItem: compareInlineAddressItem,
    },
    // Phone numbers
    {
      key: 'phoneNumbers',
      label: 'Phone',
      section: 'Phone numbers',
      expandArray: true,
    },
    // Emails
    {
      key: 'emails',
      label: 'Email',
      section: 'Emails',
      expandArray: true,
    },
    // Contacts
    {
      key: 'contacts',
      label: 'Contact',
      section: 'Contacts',
      expandArray: true,
    },
    // Urls
    {
      key: 'urls',
      label: 'URL',
      section: 'Urls',
      expandArray: true,
    },
    // Social media links
    {
      key: 'socialMediaLinks',
      label: 'Social media link',
      section: 'Social media links',
      expandArray: true,
    },
    {
      key: 'socialMedia',
      label: 'Social media',
      section: 'Social media links',
    },
    // Emergency Contacts
    {
      key: 'emergencyContacts',
      label: 'Emergency contact',
      section: 'Emergency Contacts',
      expandArray: true,
      compareItem: compareEmergencyContactItem,
    },
    // Verification
    { key: 'isVerified', label: 'Verified', section: 'Verification' },
    // Photos
    {
      key: 'files',
      label: 'Photos',
      section: 'Photos',
      renderType: 'photo',
      compare: compareFiles,
    },
  ],
  getRecordTitle: (r) => {
    const o = r as IMasterNameOrganization;
    const id = o.uniqueId != null ? generateUniqueId('ORG', o.uniqueId) : '';
    return [id, o.name].filter(Boolean).join(' · ');
  },
  getRecordSubtitle: (r) => {
    const o = r as IMasterNameOrganization;
    // Detail GET populates the scalar `address` ref (empty for form-saved
    // records); fall back to the first embedded address so the selected-record
    // card matches the search result.
    const addr = (o.address ?? o.addresses?.[0]) as any;
    const addressText = addr
      ? getAddressLocation(addr) || addr?.addressLabel || ''
      : '';
    const rawPhone = o.phoneNumbers?.[0]?.phoneNumber ?? null;
    const phone = rawPhone ? formatPhoneNumber(rawPhone) || rawPhone : null;
    return [addressText || null, phone].filter(Boolean).join(' · ');
  },
  icon: BusinessOutlinedIcon,
};

// ---------------------------------------------------------------------------
// Vehicle - formatters
// ---------------------------------------------------------------------------

function resolvePlateState(state: unknown): string | null {
  if (!state) return null;
  if (typeof state === 'object') {
    return (state as any).name ?? (state as any).code ?? null;
  }
  return USStates.find((s) => s.value === state)?.label ?? String(state);
}

function resolvePlateType(type: unknown): string | null {
  if (!type) return null;
  if (typeof type === 'object') {
    return (type as any).description ?? (type as any).code ?? null;
  }
  return String(type);
}

function formatSingleLicensePlate(value: unknown): string {
  if (!value || typeof value !== 'object') return EMPTY;
  const p = value as any;
  const isActive = p?.isActive;
  const parts = [
    p?.number ? `#${p.number}` : null,
    resolvePlateState(p?.state),
    p?.year ? `(${p.year})` : null,
    resolvePlateType(p?.type),
  ].filter(Boolean);
  return (
    [isActive ? '(Active)' : null, ...parts].filter(Boolean).join(' · ') ||
    EMPTY
  );
}

function compareLicensePlateItem(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const pa = a as any;
  const pb = b as any;
  const stateA =
    pa.state && typeof pa.state === 'object' ? pa.state.code : pa.state;
  const stateB =
    pb.state && typeof pb.state === 'object' ? pb.state.code : pb.state;
  const typeA = pa.type && typeof pa.type === 'object' ? pa.type.code : pa.type;
  const typeB = pb.type && typeof pb.type === 'object' ? pb.type.code : pb.type;
  return (
    pa.number === pb.number &&
    stateA === stateB &&
    String(pa.year ?? '') === String(pb.year ?? '') &&
    typeA === typeB &&
    (pa.isActive ?? false) === (pb.isActive ?? false)
  );
}

export type MergeFormatContext = {
  vehicleTypeLabels?: Record<string, string>;
};

function formatVehicleField(
  key: string,
  value: unknown,
  ctx?: MergeFormatContext,
): string {
  if (isEmpty(value)) return EMPTY;

  if (key.includes('::')) {
    const parentKey = key.split('::')[0];
    if (parentKey === 'licensePlates') return formatSingleLicensePlate(value);
    return String(value);
  }

  switch (key) {
    case '__vehicleDescription': {
      if (typeof value !== 'object' || !value) return EMPTY;
      const d = value as Record<string, unknown>;
      const lines: string[] = [];
      const rawType = d.type;
      const vehicleType =
        rawType && typeof rawType === 'object'
          ? (rawType as any).label ??
            (rawType as any).name ??
            (rawType as any).code
          : typeof rawType === 'string'
          ? ctx?.vehicleTypeLabels?.[rawType] ??
            rawType
              .toLowerCase()
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase())
          : null;
      if (vehicleType) lines.push(`Type: ${vehicleType}`);
      const makeName =
        d.make && typeof d.make === 'object'
          ? (d.make as any).name ?? ''
          : d.make;
      if (makeName) lines.push(`Make: ${makeName}`);
      const modelName =
        d.model && typeof d.model === 'object'
          ? (d.model as any).name ?? ''
          : d.model;
      if (modelName) lines.push(`Model: ${modelName}`);
      if (d.year) lines.push(`Year: ${d.year}`);
      const colorName =
        d.color && typeof d.color === 'object'
          ? (d.color as any).name ?? ''
          : d.color;
      if (colorName) lines.push(`Color: ${colorName}`);
      if (d.style) lines.push(`Style: ${d.style}`);
      return lines.join('\n') || EMPTY;
    }
    case 'color':
      if (typeof value === 'object' && value !== null) {
        return (value as any).name ?? String(value);
      }
      return String(value);
    case 'licensePlates':
      return Array.isArray(value)
        ? value
            .map((p: any) => {
              const state =
                p?.state && typeof p.state === 'object'
                  ? (p.state as any).name ?? (p.state as any).code
                  : typeof p?.state === 'string'
                  ? USStates.find((s) => s.value === p.state)?.label ?? p.state
                  : null;
              const plateType =
                p?.type && typeof p.type === 'object'
                  ? (p.type as any).description ?? (p.type as any).code
                  : p?.type ?? null;
              const parts = [
                p?.number ? `#${p.number}` : null,
                state ?? null,
                p?.year ? `(${p.year})` : null,
                plateType ?? null,
              ].filter(Boolean);
              return parts.join(' · ');
            })
            .filter(Boolean)
            .join('\n') || EMPTY
        : String(value);
    case 'owner': {
      if (!value) return EMPTY;
      // List endpoint may return owner as a bare string ID (not populated)
      if (typeof value !== 'object') return String(value);
      const o = value as any;
      return formatPersonName(o) || o.name || EMPTY;
    }
    case 'isVerified':
      return value ? 'Yes' : 'No';
    case 'files':
      return Array.isArray(value)
        ? `${value.length} file${value.length !== 1 ? 's' : ''}`
        : String(value);
    default:
      return String(value);
  }
}

// ---------------------------------------------------------------------------
// Vehicle config
// ---------------------------------------------------------------------------

// Vehicle description fields (type/make/model/year/color/style) are grouped
// into a single virtual conflict row. Different vehicle types have different
// valid combinations, so it makes sense to accept or reject the description
// as a unit rather than field by field.

const vehicleConfig: MergeTypeConfig = {
  fields: [
    // Vehicle information (VIN + plates, matching the view)
    { key: 'vin', label: 'VIN', section: 'Vehicle information' },
    {
      key: 'licensePlates',
      label: 'License plate',
      section: 'Vehicle information',
      expandArray: true,
      compareItem: compareLicensePlateItem,
    },
    // Vehicle description
    {
      key: '__vehicleDescription',
      label: 'Vehicle description',
      section: 'Vehicle description',
      virtual: { keys: ['type', 'make', 'model', 'year', 'color', 'style'] },
    },
    {
      key: 'uniqueCharacteristics',
      label: 'Unique characteristics',
      section: 'Vehicle description',
    },
    // Registered owner information
    {
      key: 'owner',
      label: 'Registered owner',
      section: 'Registered owner information',
    },
    // Verification status
    {
      key: 'isVerified',
      label: 'Verified',
      section: 'Verification status',
    },
    // Photos
    {
      key: 'files',
      label: 'Photos',
      section: 'Photos',
      renderType: 'photo',
      compare: compareFiles,
    },
  ],
  getRecordTitle: (r) => {
    const v = r as IMasterVehicle;
    const lp = v.licensePlate?.number ?? v.licensePlates?.[0]?.number ?? '';
    const id = v.uniqueId != null ? generateUniqueId('VEH', v.uniqueId) : '';
    return [id, lp].filter(Boolean).join(' · ');
  },
  getRecordSubtitle: (r) => {
    const v = r as IMasterVehicle;
    return [v.make, v.model, v.color?.name ?? ''].filter(Boolean).join(' · ');
  },
  icon: DirectionsCarOutlinedIcon,
};

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

export const mergeConfigs: Record<MasterIndexType, MergeTypeConfig> = {
  persons: personConfig,
  address: addressConfig,
  organizations: organizationConfig,
  vehicles: vehicleConfig,
};

export function getConflicts(
  type: MasterIndexType,
  current: AnyMasterRecord,
  selected: AnyMasterRecord,
): MergeConflict[] {
  const config = mergeConfigs[type];
  const conflicts: MergeConflict[] = [];

  for (const field of config.fields) {
    if (field.expandArray) {
      const currentArr = getNestedValue(current, field.key);
      const selectedArr = getNestedValue(selected, field.key);
      const cArr = Array.isArray(currentArr) ? currentArr : [];
      const sArr = Array.isArray(selectedArr) ? selectedArr : [];
      const maxLen = Math.max(cArr.length, sArr.length);
      for (let i = 0; i < maxLen; i++) {
        const cv = i < cArr.length ? cArr[i] : undefined;
        const sv = i < sArr.length ? sArr[i] : undefined;
        const itemEqual = field.compareItem
          ? field.compareItem(cv, sv)
          : isEqual(cv, sv);
        if (!itemEqual && !(isEmpty(cv) && isEmpty(sv))) {
          conflicts.push({
            field: {
              key: `${field.key}::${i}`,
              label: `${field.label} ${i + 1}`,
              section: field.section,
            },
            currentValue: cv,
            selectedValue: sv,
            parentKey: field.key,
            arrayIndex: i,
          });
        }
      }
      continue;
    }

    let currentValue: unknown;
    let selectedValue: unknown;

    if (field.virtual) {
      currentValue = Object.fromEntries(
        field.virtual.keys.map((k) => [k, getNestedValue(current, k)]),
      );
      selectedValue = Object.fromEntries(
        field.virtual.keys.map((k) => [k, getNestedValue(selected, k)]),
      );
    } else {
      currentValue = getNestedValue(current, field.key);
      selectedValue = getNestedValue(selected, field.key);
    }

    const equal = field.compare
      ? field.compare(currentValue, selectedValue)
      : isEqual(currentValue, selectedValue);

    if (!equal && !(isEmpty(currentValue) && isEmpty(selectedValue))) {
      conflicts.push({ field, currentValue, selectedValue });
    }
  }

  return conflicts;
}

export function formatMergeValue(
  type: MasterIndexType,
  key: string,
  value: unknown,
  ctx?: MergeFormatContext,
): string {
  if (isEmpty(value)) return EMPTY;
  switch (type) {
    case 'persons':
      return formatPersonField(key, value);
    case 'vehicles':
      return formatVehicleField(key, value, ctx);
    case 'organizations':
      return formatOrgField(key, value);
    case 'address':
      return formatAddressField(key, value);
    default:
      return typeof value === 'string' ? value : String(value);
  }
}

export function formatFieldValue(value: unknown): string {
  if (isEmpty(value)) return EMPTY;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number')
    return String(value);
  if (Array.isArray(value)) {
    return `${value.length} item${value.length !== 1 ? 's' : ''}`;
  }
  return JSON.stringify(value);
}
